import { sendRequest } from "@infrastructure/api/api";
import type ApiResponse from "@core/types/apiResponse";
import type { ErrorResponse } from "@core/types/errorResponse";

export type DepartmentReportListItem = {
  church_id?: number;
  district_id?: number;
  nation_id?: number;

  department_id: number;
  department_name: string;

  department_report_id: number;
  report_tag_id?: number;
  report_tag_name?: string;
  report_tag_since?: string | null;
  report_tag_until?: string | null;

  department_report_status_name: string;
  department_report_status_id: number;

  last_updated: string | null;
};

export type PaginatedDepartmentReportQueryResponse = {
  current_page: number;
  data: DepartmentReportListItem[];
  first_page_url?: string | null;
  from?: number | null;
  last_page: number;
  last_page_url?: string | null;
  next_page_url?: string | null;
  path?: string | null;
  per_page: number;
  prev_page_url?: string | null;
  to?: number | null;
  total: number;
};

/**
 * Leadership report type filter (matches the API's LEADERSHIP_DEPARTMENT_TYPES).
 * When set, the query hits the `.../department/report/leadership/query` endpoint.
 */
export type LeadershipType = "PASTOR" | "DISTRICT_OVERSEER" | "GENERAL";

type CommonQueryArgs = {
  page?: number;
  perPage?: number;
  departmentId?: number;
  departmentReportId?: number;
  reportTagId?: number;
  reportTemplateId?: number;
  reportStatusId?: number;
  freetext?: string;
  /** When present, query the leadership endpoint filtered to this leadership type. */
  leadershipType?: LeadershipType;
};

export type FetchDepartmentReportQueryArgs =
  | ({ scope: "church"; churchId: number } & CommonQueryArgs)
  | ({ scope: "district"; districtId: number } & CommonQueryArgs)
  | ({ scope: "nation"; nationId: number } & CommonQueryArgs);

function scopeBase(args: FetchDepartmentReportQueryArgs): string {
  if (args.scope === "church") return `/church/${args.churchId}`;
  if (args.scope === "district") return `/district/${args.districtId}`;
  return `/nation/${args.nationId}`;
}

function buildPath(args: FetchDepartmentReportQueryArgs): string {
  const base = scopeBase(args);
  // When a leadership type is supplied, hit the leadership query endpoint (D11 reuse).
  return args.leadershipType
    ? `${base}/department/report/leadership/query`
    : `${base}/department/report/query`;
}

export async function fetchDepartmentReportsQueryAsync(
  args: FetchDepartmentReportQueryArgs
): Promise<ApiResponse<PaginatedDepartmentReportQueryResponse, ErrorResponse>> {
  const page = args.page ?? 1;
  const perPage = args.perPage ?? 20;

  const params = new URLSearchParams({
    page: String(page),
    perPage: String(perPage),
  });

  if (args.departmentId) params.set("departmentId", String(args.departmentId));
  if ((args as any).reportTagId) params.set("reportTagId", String((args as any).reportTagId));
  if ((args as any).departmentReportId) params.set("departmentReportId", String((args as any).departmentReportId));
  if ((args as any).reportTemplateId) params.set("reportTemplateId", String((args as any).reportTemplateId));
  if ((args as any).reportStatusId) params.set("reportStatusId", String((args as any).reportStatusId));
  if ((args as any).freetext) params.set("freetext", String((args as any).freetext));
  if (args.leadershipType) params.set("leadershipType", String(args.leadershipType));

  const url = `${buildPath(args)}?${params.toString()}`;
  return sendRequest<any, PaginatedDepartmentReportQueryResponse>("GET", url);
}